// Hook para validações de usuário reutilizáveis
import { useState, useCallback } from 'react';
import { emailValidation, cpfCnpjValidation, applyCpfCnpjMask } from '../utils/validationUtils';
import { ErrorHandler } from '../utils/errorHandler';

export interface UserValidationState {
  email: {
    value: string;
    error: string;
    isValid: boolean;
    isChecking: boolean;
  };
  cpfCnpj: {
    value: string;
    error: string;
    isValid: boolean;
    isChecking: boolean;
  };
}

export function useUserValidations() {
  const [validationState, setValidationState] = useState<UserValidationState>({
    email: { value: '', error: '', isValid: true, isChecking: false },
    cpfCnpj: { value: '', error: '', isValid: true, isChecking: false },
  });

  // Função para verificar se email já existe
  const checkEmailExists = useCallback(async (email: string): Promise<boolean> => {
    try {
      const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/autenticador/emaildisponivel/${encodeURIComponent(email)}`);

      if (!response.ok) {
        return true; // Email já existe
      }

      return false; // Email disponível
    } catch (error) {
      const errors = ErrorHandler.processApiError(error);
      console.error('Erro ao verificar email:', errors.join(', '));
      return false; // Em caso de erro, assume disponível
    }
  }, []);

  // Função para verificar se CPF/CNPJ já existe
  const checkCpfCnpjExists = useCallback(async (cpfCnpj: string): Promise<boolean> => {
    // Primeiro valida se o CPF/CNPJ é válido
    const validation = cpfCnpjValidation.validate(cpfCnpj);
    if (!validation.isValid) {
      return false; // Não prossegue se for inválido
    }

    try {
      // Remove caracteres não alfanuméricos para enviar para a API
      const cleanValue = cpfCnpj.replace(/[^a-zA-Z0-9]/g, '');
      const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/autenticador/cnpjdisponivel/${encodeURIComponent(cleanValue)}`);

      if (!response.ok) {
        return true; // CPF/CNPJ já existe
      }

      return false; // CPF/CNPJ disponível
    } catch (error) {
      const errors = ErrorHandler.processApiError(error);
      console.error('Erro ao verificar CPF/CNPJ:', errors.join(', '));
      return false; // Em caso de erro, assume disponível
    }
  }, []);

  // Validação de email com verificação de disponibilidade
  const validateEmail = useCallback(async (email: string) => {
    setValidationState(prev => ({
      ...prev,
      email: { ...prev.email, value: email, isChecking: true, error: '', isValid: false }
    }));

    // Validação básica de formato
    const basicValidation = emailValidation.validate(email);
    if (!basicValidation.isValid) {
      setValidationState(prev => ({
        ...prev,
        email: { ...prev.email, error: basicValidation.errorMessage!, isValid: false, isChecking: false }
      }));
      return false;
    }

    // Verificar disponibilidade
    const exists = await checkEmailExists(email);
    if (exists) {
      setValidationState(prev => ({
        ...prev,
        email: { ...prev.email, error: 'Este e-mail já está em uso', isValid: false, isChecking: false }
      }));
      return false;
    }

    setValidationState(prev => ({
      ...prev,
      email: { ...prev.email, error: '', isValid: true, isChecking: false }
    }));
    return true;
  }, [checkEmailExists]);

  // Validação de CPF/CNPJ com verificação de disponibilidade
  const validateCpfCnpj = useCallback(async (cpfCnpj: string) => {
    setValidationState(prev => ({
      ...prev,
      cpfCnpj: { ...prev.cpfCnpj, value: cpfCnpj, isChecking: true, error: '', isValid: false }
    }));

    // Validação básica de formato
    const basicValidation = cpfCnpjValidation.validate(cpfCnpj);
    if (!basicValidation.isValid) {
      setValidationState(prev => ({
        ...prev,
        cpfCnpj: { ...prev.cpfCnpj, error: basicValidation.errorMessage!, isValid: false, isChecking: false }
      }));
      return false;
    }

    // Verificar disponibilidade
    const exists = await checkCpfCnpjExists(cpfCnpj);
    if (exists) {
      setValidationState(prev => ({
        ...prev,
        cpfCnpj: { ...prev.cpfCnpj, error: 'Este CPF/CNPJ já está cadastrado', isValid: false, isChecking: false }
      }));
      return false;
    }

    setValidationState(prev => ({
      ...prev,
      cpfCnpj: { ...prev.cpfCnpj, error: '', isValid: true, isChecking: false }
    }));
    return true;
  }, [checkCpfCnpjExists]);

  // Aplicar máscara ao CPF/CNPJ
  const applyCpfCnpjMaskToValue = useCallback((value: string) => {
    return applyCpfCnpjMask(value);
  }, []);

  // Resetar validações
  const resetValidations = useCallback(() => {
    setValidationState({
      email: { value: '', error: '', isValid: true, isChecking: false },
      cpfCnpj: { value: '', error: '', isValid: true, isChecking: false },
    });
  }, []);

  return {
    // Validações de unicidade (para compatibilidade com useRegister)
    checkEmailExists,
    checkCpfCnpjExists,
    
    // Estados de validação
    validationState,
    
    // Funções de validação com debounce
    validateEmail,
    validateCpfCnpj,
    
    // Utilitários
    applyCpfCnpjMaskToValue,
    resetValidations,
  };
}